import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';

// Helper functions
const countWords = (text) => {
  if (!text) return 0;
  return text.trim().split(/\s+/).filter(w => w.length > 0).length;
};

const validateFieldWithLimits = (fieldName, value, limits) => {
  const fieldLimit = limits[fieldName];
  if (!fieldLimit) return { valid: true, status: 'good' };

  const wordCount = countWords(value);
  let status = 'good';

  if (wordCount === 0) {
    status = 'empty';
  } else if (wordCount < fieldLimit.min) {
    status = 'short';
  } else if (wordCount > fieldLimit.max) {
    status = 'long';
  } else if (wordCount > fieldLimit.max - 5) {
    status = 'warning';
  }

  return {
    valid: wordCount === 0 || (wordCount >= fieldLimit.min && wordCount <= fieldLimit.max),
    wordCount,
    min: fieldLimit.min,
    max: fieldLimit.max,
    target: fieldLimit.target,
    status
  };
};

const getStatusColor = (status) => {
  switch (status) {
    case 'good': return '#28a745';
    case 'warning': return '#ffc107';
    case 'short': return '#17a2b8';
    case 'long': return '#dc3545';
    case 'empty': return '#6c757d';
    default: return '#6c757d';
  }
};

const getStatusIcon = (status) => {
  switch (status) {
    case 'good': return '✓';
    case 'warning': return '⚠';
    case 'short': return '↓';
    case 'long': return '✗';
    default: return '';
  }
};

// Text field component with word counter
const TextFieldWithCounter = ({ label, fieldName, rows = 3, placeholder, value, limits, validation, onChange }) => {
  const val = validation || {};

  return (
    <div className="mb-3">
      <label className="form-label fw-bold" style={{ fontSize: '0.85rem' }}>
        {label}
        {limits && (
          <span className="ms-2 fw-normal text-muted" style={{ fontSize: '0.75rem' }}>
            ({limits.target})
          </span>
        )}
      </label>
      <textarea
        className="form-control form-control-sm"
        rows={rows}
        value={value || ''}
        onChange={(e) => onChange(fieldName, e.target.value)}
        placeholder={placeholder}
        spellCheck="true"
        autoComplete="off"
        autoCorrect="on"
        style={{
          borderColor: val.status === 'long' ? '#dc3545' : undefined,
          fontSize: '0.85rem'
        }}
      />
      {limits && (
        <div className="d-flex justify-content-between mt-1" style={{ fontSize: '0.75rem' }}>
          <span style={{ color: getStatusColor(val.status) }}>
            {getStatusIcon(val.status)} {val.wordCount || 0} / {limits.max} words
          </span>
          {val.status === 'short' && <span className="text-info">Need {limits.min - (val.wordCount || 0)} more</span>}
          {val.status === 'long' && <span className="text-danger">{(val.wordCount || 0) - limits.max} over</span>}
        </div>
      )}
    </div>
  );
};

// Budget constants for dynamic word limits
const OPINION_BUDGET = 180;
const PORTFOLIO_BUDGET = 200;

const InlineContentEditor = ({ apirCode, fundName, onSave, onClose }) => {
  const [formData, setFormData] = useState({});
  const [fieldLimits, setFieldLimits] = useState({});
  const [validation, setValidation] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [hasExistingContent, setHasExistingContent] = useState(false);
  const [overviewCount, setOverviewCount] = useState(3);
  const [opinionCount, setOpinionCount] = useState(4);
  const [portfolioCount, setPortfolioCount] = useState(4);

  // Load content when apirCode changes
  useEffect(() => {
    if (apirCode) {
      loadFundContent(apirCode);
    }
  }, [apirCode]);

  const fetchFieldLimits = async (opinionCt = 4, portfolioCt = 4) => {
    try {
      const response = await api.get('/api/admin/fund-content/field-limits', {
        params: { opinion_count: opinionCt, portfolio_count: portfolioCt }
      });
      setFieldLimits(response.data);
    } catch (err) {
      console.error('Error fetching field limits:', err);
    }
  };

  const loadFundContent = async (apir) => {
    setIsLoading(true);
    try {
      const response = await api.get(`/api/admin/fund-content/${apir}`);
      setFormData(response.data);
      setHasExistingContent(true);

      // Auto-detect existing field counts (minimum 1)
      let detectedOverviewCount = 1;
      for (let i = 3; i >= 1; i--) {
        if (response.data[`overview_paragraph_${i}`]?.trim()) {
          detectedOverviewCount = i;
          break;
        }
      }

      let detectedOpinionCount = 1;
      for (let i = 6; i >= 1; i--) {
        if (response.data[`opinion_${i}`]?.trim()) {
          detectedOpinionCount = i;
          break;
        }
      }

      let detectedPortfolioCount = 1;
      for (let i = 6; i >= 1; i--) {
        if (response.data[`portfolio_${i}`]?.trim()) {
          detectedPortfolioCount = i;
          break;
        }
      }

      setOverviewCount(detectedOverviewCount);
      setOpinionCount(detectedOpinionCount);
      setPortfolioCount(detectedPortfolioCount);

      // Fetch limits with detected counts and validate
      const limitsResponse = await api.get('/api/admin/fund-content/field-limits', {
        params: { opinion_count: detectedOpinionCount, portfolio_count: detectedPortfolioCount }
      });
      setFieldLimits(limitsResponse.data);

      // Validate with the fetched limits
      const newValidation = {};
      Object.keys(limitsResponse.data).forEach(field => {
        newValidation[field] = validateFieldWithLimits(field, response.data[field] || '', limitsResponse.data);
      });
      setValidation(newValidation);
    } catch (err) {
      if (err.response?.status === 404) {
        // No content yet - start with empty form
        const emptyForm = {
          apir_code: apir,
          fund_name: fundName || '',
          overview_paragraph_1: '',
          overview_paragraph_2: '',
          overview_paragraph_3: '',
          opinion_1: '',
          opinion_2: '',
          opinion_3: '',
          opinion_4: '',
          opinion_5: '',
          opinion_6: '',
          portfolio_1: '',
          portfolio_2: '',
          portfolio_3: '',
          portfolio_4: '',
          portfolio_5: '',
          portfolio_6: '',
          fund_summary: '',
        };
        setFormData(emptyForm);
        setHasExistingContent(false);
        setOverviewCount(3);
        setOpinionCount(4);
        setPortfolioCount(4);

        // Fetch default limits
        const limitsResponse = await api.get('/api/admin/fund-content/field-limits', {
          params: { opinion_count: 4, portfolio_count: 4 }
        });
        setFieldLimits(limitsResponse.data);
      } else {
        setMessage({ type: 'error', text: 'Failed to load fund content' });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const validateAllFields = useCallback((data) => {
    const newValidation = {};
    Object.keys(fieldLimits).forEach(field => {
      newValidation[field] = validateFieldWithLimits(field, data[field] || '', fieldLimits);
    });
    setValidation(newValidation);
  }, [fieldLimits]);

  const handleFieldChange = useCallback((fieldName, value) => {
    setFormData(prev => ({ ...prev, [fieldName]: value }));
    setValidation(prev => ({
      ...prev,
      [fieldName]: validateFieldWithLimits(fieldName, value, fieldLimits)
    }));
  }, [fieldLimits]);

  // Handlers for adding/removing opinion points
  const handleAddOpinion = async () => {
    if (opinionCount < 6) {
      const newCount = opinionCount + 1;
      setOpinionCount(newCount);
      await fetchFieldLimits(newCount, portfolioCount);
    }
  };

  const handleRemoveOpinion = async () => {
    if (opinionCount > 1) {
      const newCount = opinionCount - 1;
      // Clear the removed field
      setFormData(prev => ({ ...prev, [`opinion_${opinionCount}`]: '' }));
      setOpinionCount(newCount);
      await fetchFieldLimits(newCount, portfolioCount);
    }
  };

  // Handlers for adding/removing portfolio points
  const handleAddPortfolio = async () => {
    if (portfolioCount < 6) {
      const newCount = portfolioCount + 1;
      setPortfolioCount(newCount);
      await fetchFieldLimits(opinionCount, newCount);
    }
  };

  const handleRemovePortfolio = async () => {
    if (portfolioCount > 1) {
      const newCount = portfolioCount - 1;
      // Clear the removed field
      setFormData(prev => ({ ...prev, [`portfolio_${portfolioCount}`]: '' }));
      setPortfolioCount(newCount);
      await fetchFieldLimits(opinionCount, newCount);
    }
  };

  // Handlers for adding/removing overview paragraphs
  const handleAddOverview = () => {
    if (overviewCount < 3) {
      setOverviewCount(c => c + 1);
    }
  };

  const handleRemoveOverview = () => {
    if (overviewCount > 1) {
      setFormData(prev => ({ ...prev, [`overview_paragraph_${overviewCount}`]: '' }));
      setOverviewCount(c => c - 1);
    }
  };

  // Calculate section totals
  const calculateSectionTotal = (prefix, count) => {
    let total = 0;
    for (let i = 1; i <= count; i++) {
      total += countWords(formData[`${prefix}_${i}`] || '');
    }
    return total;
  };

  const opinionTotal = calculateSectionTotal('opinion', opinionCount);
  const portfolioTotal = calculateSectionTotal('portfolio', portfolioCount);

  const handleSave = async () => {
    setIsSaving(true);
    setMessage(null);

    try {
      if (hasExistingContent) {
        await api.put(`/api/admin/fund-content/${formData.apir_code}`, formData);
      } else {
        await api.post('/api/admin/fund-content', formData);
        setHasExistingContent(true);
      }

      setMessage({ type: 'success', text: 'Content saved!' });

      // Notify parent to refresh report
      if (onSave) {
        onSave();
      }
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.detail || 'Failed to save' });
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="text-center py-4">
        <div className="spinner-border spinner-border-sm"></div>
        <p className="mt-2 small text-muted">Loading content...</p>
      </div>
    );
  }

  return (
    <div className="inline-content-editor">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-start mb-3 pb-2 border-bottom">
        <div>
          <h6 className="mb-0" style={{ color: '#072040' }}>Edit Content</h6>
          <small className="text-muted">{fundName}</small>
        </div>
        <div className="d-flex gap-2">
          <button
            className="btn btn-sm btn-primary"
            onClick={handleSave}
            disabled={isSaving}
            style={{ backgroundColor: '#072040', borderColor: '#072040' }}
          >
            {isSaving ? 'Saving...' : 'Save'}
          </button>
          <button
            className="btn btn-sm btn-outline-secondary"
            onClick={onClose}
            title="Close editor"
          >
            ✕
          </button>
        </div>
      </div>

      {/* Message */}
      {message && (
        <div className={`alert alert-${message.type === 'error' ? 'danger' : 'success'} py-2 small`}>
          {message.text}
        </div>
      )}

      {/* Form */}
      <div className="editor-form">
        {/* Overview Section */}
        <div className="d-flex justify-content-between align-items-center border-bottom pb-2 mb-3">
          <h6 className="mb-0" style={{ color: '#072040', fontSize: '0.9rem' }}>OVERVIEW</h6>
          <div className="d-flex gap-2">
            {overviewCount < 3 && (
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                onClick={handleAddOverview}
              >
                + Add Paragraph
              </button>
            )}
            {overviewCount > 1 && (
              <button
                type="button"
                className="btn btn-sm btn-outline-danger"
                onClick={handleRemoveOverview}
              >
                Remove
              </button>
            )}
          </div>
        </div>
        {[1, 2, 3].slice(0, overviewCount).map(i => (
          <TextFieldWithCounter
            key={`overview_paragraph_${i}`}
            label={`Paragraph ${i}`}
            fieldName={`overview_paragraph_${i}`}
            rows={3}
            placeholder={i === 1 ? "Describe the fund manager, history, and team..." :
                        i === 2 ? "Describe the investment strategy..." :
                        "Describe performance and objectives..."}
            value={formData[`overview_paragraph_${i}`]}
            limits={fieldLimits[`overview_paragraph_${i}`]}
            validation={validation[`overview_paragraph_${i}`]}
            onChange={handleFieldChange}
          />
        ))}

        {/* Drummond's Opinion */}
        <div className="d-flex justify-content-between align-items-center border-bottom pb-2 mb-3 mt-4">
          <h6 className="mb-0" style={{ color: '#072040', fontSize: '0.9rem' }}>DRUMMOND'S OPINION</h6>
          <small style={{ color: opinionTotal > OPINION_BUDGET ? '#dc3545' : '#6c757d' }}>
            {opinionTotal} / {OPINION_BUDGET} words
          </small>
        </div>
        {[1, 2, 3, 4, 5, 6].slice(0, opinionCount).map(i => (
          <TextFieldWithCounter
            key={`opinion_${i}`}
            label={`Point ${i}`}
            fieldName={`opinion_${i}`}
            rows={2}
            value={formData[`opinion_${i}`]}
            limits={fieldLimits[`opinion_${i}`]}
            validation={validation[`opinion_${i}`]}
            onChange={handleFieldChange}
          />
        ))}
        <div className="d-flex gap-2 mb-3">
          {opinionCount < 6 && (
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              onClick={handleAddOpinion}
            >
              + Add Point
            </button>
          )}
          {opinionCount > 1 && (
            <button
              type="button"
              className="btn btn-sm btn-outline-danger"
              onClick={handleRemoveOpinion}
            >
              Remove Point {opinionCount}
            </button>
          )}
        </div>

        {/* Portfolio Construction */}
        <div className="d-flex justify-content-between align-items-center border-bottom pb-2 mb-3 mt-4">
          <h6 className="mb-0" style={{ color: '#072040', fontSize: '0.9rem' }}>PORTFOLIO CONSIDERATIONS</h6>
          <small style={{ color: portfolioTotal > PORTFOLIO_BUDGET ? '#dc3545' : '#6c757d' }}>
            {portfolioTotal} / {PORTFOLIO_BUDGET} words
          </small>
        </div>
        {[1, 2, 3, 4, 5, 6].slice(0, portfolioCount).map(i => (
          <TextFieldWithCounter
            key={`portfolio_${i}`}
            label={`Point ${i}`}
            fieldName={`portfolio_${i}`}
            rows={2}
            value={formData[`portfolio_${i}`]}
            limits={fieldLimits[`portfolio_${i}`]}
            validation={validation[`portfolio_${i}`]}
            onChange={handleFieldChange}
          />
        ))}
        <div className="d-flex gap-2 mb-3">
          {portfolioCount < 6 && (
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              onClick={handleAddPortfolio}
            >
              + Add Point
            </button>
          )}
          {portfolioCount > 1 && (
            <button
              type="button"
              className="btn btn-sm btn-outline-danger"
              onClick={handleRemovePortfolio}
            >
              Remove Point {portfolioCount}
            </button>
          )}
        </div>

        {/* Fund Summary */}
        <h6 className="border-bottom pb-2 mb-3 mt-4" style={{ color: '#072040', fontSize: '0.9rem' }}>FUND SUMMARY</h6>
        <TextFieldWithCounter
          label="Summary (sidebar)"
          fieldName="fund_summary"
          rows={2}
          placeholder="Brief summary for sidebar..."
          value={formData.fund_summary}
          limits={fieldLimits.fund_summary}
          validation={validation.fund_summary}
          onChange={handleFieldChange}
        />
      </div>
    </div>
  );
};

export default InlineContentEditor;
